// bluaIQ — Takeoff category color system (19-category construction sequence)
// Usage: <div style={{ borderLeft: `4px solid ${getCategoryColor(item.category)}` }} />
// Shared across Takeoff, Budget, and Material Order Schedule modules.

export const TAKEOFF_CATEGORIES = [
  'FOUNDATION',
  'FRAMING',
  'EXTERIOR',         // Siding, Hardie, LP SmartSide, stone, brick
  'ROOFING',
  'WINDOWS',
  'DOORS',
  'PLUMBING_ROUGH',   // pipes, supply lines, drains, vents, water heater
  'ELECTRICAL_ROUGH', // wiring, panels, boxes, conduit, breakers
  'HVAC',
  'INSULATION',
  'DRYWALL',
  'PLUMBING_FIXTURES', // sinks, faucets, toilets, shower pans, tubs
  'LIGHTING_FANS',     // recessed lights, chandeliers, ceiling fans, sconces
  'PAINT',
  'CABINETS',
  'COUNTERTOPS',
  'FLOORING',
  'APPLIANCES',
  'LANDSCAPING',
] as const;

export type TakeoffCategory = typeof TAKEOFF_CATEGORIES[number];

// Construction sequence order (index = display order)
export const CONSTRUCTION_ORDER: Record<TakeoffCategory, number> = {
  FOUNDATION: 0,
  FRAMING: 1,
  EXTERIOR: 2,
  ROOFING: 3,
  WINDOWS: 4,
  DOORS: 5,
  PLUMBING_ROUGH: 6,
  ELECTRICAL_ROUGH: 7,
  HVAC: 8,
  INSULATION: 9,
  DRYWALL: 10,
  PLUMBING_FIXTURES: 11,
  LIGHTING_FANS: 12,
  PAINT: 13,
  CABINETS: 14,
  COUNTERTOPS: 15,
  FLOORING: 16,
  APPLIANCES: 17,
  LANDSCAPING: 18,
};

export const TAKEOFF_CATEGORY_COLORS: Record<TakeoffCategory, string> = {
  FOUNDATION:        '#6B7280', // concrete gray
  FRAMING:           '#A16207', // lumber brown
  EXTERIOR:          '#059669', // hardie green
  ROOFING:           '#B91C1C', // terracotta red
  WINDOWS:           '#0EA5E9', // sky blue
  DOORS:             '#7C3AED', // purple
  PLUMBING_ROUGH:    '#06B6D4', // cyan
  ELECTRICAL_ROUGH:  '#F59E0B', // amber
  HVAC:              '#0D9488', // teal
  INSULATION:        '#EC4899', // salmon pink
  DRYWALL:           '#D4A574', // chalk tan
  PLUMBING_FIXTURES: '#0891B2', // darker cyan
  LIGHTING_FANS:     '#EAB308', // gold/yellow
  PAINT:             '#8B5CF6', // violet
  CABINETS:          '#EA580C', // burnt orange
  COUNTERTOPS:       '#475569', // slate
  FLOORING:          '#78350F', // dark brown
  APPLIANCES:        '#4338CA', // indigo
  LANDSCAPING:       '#65A30D', // olive green
};

// Spanish + English labels for i18n
export const TAKEOFF_CATEGORY_LABELS: Record<TakeoffCategory, { en: string; es: string }> = {
  FOUNDATION:        { en: 'Foundation',             es: 'Cimentación' },
  FRAMING:           { en: 'Framing',                es: 'Estructura' },
  EXTERIOR:          { en: 'Exterior / Siding',      es: 'Exterior / Revestimiento' },
  ROOFING:           { en: 'Roofing',                es: 'Techos' },
  WINDOWS:           { en: 'Windows',                es: 'Ventanas' },
  DOORS:             { en: 'Doors',                  es: 'Puertas' },
  PLUMBING_ROUGH:    { en: 'Plumbing — Rough-In',    es: 'Preinstalación de Plomería' },
  ELECTRICAL_ROUGH:  { en: 'Electrical — Rough-In',  es: 'Preinstalación Eléctrica' },
  HVAC:              { en: 'HVAC',                   es: 'Clima' },
  INSULATION:        { en: 'Insulation',             es: 'Aislamiento' },
  DRYWALL:           { en: 'Drywall',                es: 'Tablaroca' },
  PLUMBING_FIXTURES: { en: 'Plumbing — Fixtures',    es: 'Acabados de Plomería' },
  LIGHTING_FANS:     { en: 'Lighting & Fans',        es: 'Acabados Eléctricos' },
  PAINT:             { en: 'Paint',                  es: 'Pintura' },
  CABINETS:          { en: 'Cabinets',               es: 'Gabinetes' },
  COUNTERTOPS:       { en: 'Countertops',            es: 'Cubiertas' },
  FLOORING:          { en: 'Flooring',               es: 'Pisos' },
  APPLIANCES:        { en: 'Appliances',             es: 'Electrodomésticos' },
  LANDSCAPING:       { en: 'Landscaping',            es: 'Jardinería' },
};

const FALLBACK_COLOR = '#6B7280';

function normalizeKey(category: string): string {
  return category.toUpperCase().replace(/[\s&\-]+/g, '_');
}

/**
 * Get hex color for a takeoff category. Case-insensitive, falls back to neutral gray.
 */
export function getCategoryColor(category: string | null | undefined): string {
  if (!category) return FALLBACK_COLOR;
  const key = normalizeKey(category) as TakeoffCategory;
  return TAKEOFF_CATEGORY_COLORS[key] ?? FALLBACK_COLOR;
}

/**
 * Get localized label for a takeoff category. Defaults to Spanish (app is ES-first).
 */
export function getCategoryLabel(category: string | null | undefined, lang: 'en' | 'es' = 'es'): string {
  if (!category) return '';
  const key = normalizeKey(category) as TakeoffCategory;
  const labels = TAKEOFF_CATEGORY_LABELS[key];
  if (labels) return labels[lang];
  // Unknown category — return a human-readable version of the raw string
  return category.replace(/_/g, ' ');
}

/**
 * Tailwind-friendly style object for border-left accent.
 */
export function categoryBorderStyle(category: string | null | undefined) {
  return { borderLeft: `4px solid ${getCategoryColor(category)}` };
}

/**
 * Sort items by construction sequence (FOUNDATION first, LANDSCAPING last).
 * Unknown categories sink to the bottom.
 */
export function sortByConstructionOrder<T extends { category: string }>(items: T[]): T[] {
  return [...items].sort((a, b) => {
    const keyA = normalizeKey(a.category) as TakeoffCategory;
    const keyB = normalizeKey(b.category) as TakeoffCategory;
    const orderA = CONSTRUCTION_ORDER[keyA] ?? 99;
    const orderB = CONSTRUCTION_ORDER[keyB] ?? 99;
    return orderA - orderB;
  });
}

/**
 * Sort an array of category string keys by construction order.
 */
export function sortCategoryKeys(categories: string[]): string[] {
  return [...categories].sort((a, b) => {
    const keyA = normalizeKey(a) as TakeoffCategory;
    const keyB = normalizeKey(b) as TakeoffCategory;
    const orderA = CONSTRUCTION_ORDER[keyA] ?? 99;
    const orderB = CONSTRUCTION_ORDER[keyB] ?? 99;
    return orderA - orderB;
  });
}
